<template>
  <div class="bg-white modal-card rounded" style="width: 640px; max-width: 100%;">
    <div class="bg-primary-100 p-4 text-xl">
      Ajouter un document à la base de connaissances
    </div>

    <div class="px-4 py-2 text-sm text-gray-600 border-b flex items-center justify-between gap-4">
      <div>
        Disponibilité :
        <span class="font-semibold">{{ global ? 'Tous les domaines (global)' : (domainLabel || 'Domaine courant') }}</span>
      </div>
      <label class="flex items-center gap-2 text-xs whitespace-nowrap cursor-pointer">
        <t-toggle v-model="global" />
        Tous les domaines
      </label>
    </div>

    <div class="grid grid-cols-2 gap-4 overflow-y-auto p-4" style="max-height: 60vh;">
      <t-input-group class="col-span-2" label="Format">
        <t-select
          v-model="mode"
          :options="[
            { value: 'file', text: 'Fichier PDF / DOCX' },
            { value: 'text', text: 'Texte / copier-coller' }
          ]"
        />
      </t-input-group>

      <t-input-group class="col-span-2" label="Titre du document">
        <t-input v-model="form.title" placeholder="Ex : Accord télétravail 2026" />
      </t-input-group>

      <t-input-group label="Type de document">
        <t-select v-model="form.doc_type" :options="docTypeOptions" />
      </t-input-group>

      <t-input-group label="Priorité (optionnel)">
        <t-input v-model.number="form.priority" type="number" min="0" max="1000" placeholder="100" />
      </t-input-group>

      <t-input-group label="Date de signature (optionnel)">
        <t-datepicker v-model="form.signed_at" :clearable="true" />
      </t-input-group>

      <t-input-group label="Valable jusqu'au (optionnel)">
        <t-datepicker v-model="form.valid_until" :clearable="true" />
      </t-input-group>

      <template v-if="mode === 'text'">
        <t-input-group class="col-span-2" label="Contenu (50 caractères minimum)">
          <t-textarea v-model="form.content" rows="10" placeholder="Collez ici le texte du document…" />
          <span class="text-xs text-gray-400">{{ form.content.length }} caractère(s)</span>
        </t-input-group>
        <t-input-group class="col-span-2" label="URL source (optionnel)">
          <t-input v-model="form.source_url" type="url" placeholder="https://…" />
        </t-input-group>
      </template>

      <template v-else>
        <t-input-group class="col-span-2" label="Fichier PDF ou DOCX (50 Mo maximum)">
          <input
            type="file"
            accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.docx"
            class="block w-full text-sm"
            @change="onFileSelected"
          />
          <span v-if="file" class="text-xs text-gray-500">{{ file.name }}</span>
        </t-input-group>
      </template>
    </div>

    <div class="bg-gray-50 px-4 py-3 flex gap-3 justify-end">
      <t-button class="m-0" variant="secondary" :disabled="loading" @click="$parent.close()">
        Annuler
      </t-button>
      <t-button :disabled="loading || !canSubmit" @click.prevent="submit">
        {{ loading ? 'Envoi…' : 'Ajouter' }}
      </t-button>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import aiService from '@/services/ai.service'

export default {
  name: 'app-ai-upload-document',
  props: {
    domainUuid: { type: String, default: '' },
    domainLabel: { type: String, default: '' }
  },
  data () {
    return {
      mode: 'text',
      loading: false,
      global: false,
      file: null,
      form: {
        title: '',
        doc_type: 'autre',
        priority: null,
        signed_at: null,
        valid_until: null,
        content: '',
        source_url: ''
      },
      docTypeOptions: [
        { value: 'autre', text: 'Autre' },
        { value: 'accord', text: "Accord d'entreprise" },
        { value: 'avenant', text: 'Avenant' },
        { value: 'ccn', text: 'Convention collective' },
        { value: 'code_travail', text: 'Code du travail' }
      ]
    }
  },
  computed: {
    canSubmit () {
      if (!this.form.title.trim()) {
        return false
      }
      if (this.mode === 'text') {
        return this.form.content.trim().length >= 50
      }
      return !!this.file
    }
  },
  methods: {
    onFileSelected (event) {
      this.file = event.target.files && event.target.files[0] ? event.target.files[0] : null
    },
    asDate (value) {
      if (!value) {
        return undefined
      }
      const date = value instanceof Date ? value : new Date(value)
      return isNaN(date.getTime()) ? undefined : format(date, 'yyyy-MM-dd')
    },
    sharedMeta () {
      const meta = {
        title: this.form.title.trim(),
        doc_type: this.form.doc_type,
        signed_at: this.asDate(this.form.signed_at),
        valid_until: this.asDate(this.form.valid_until)
      }
      if (this.form.priority !== null && this.form.priority !== '') {
        meta.priority = Number(this.form.priority)
      }
      // Global = no domain_uuid → the backend stores domain_id NULL (shared
      // corpus), matched by every domain's queries.
      if (this.domainUuid && !this.global) {
        meta.domain_uuid = this.domainUuid
      }
      return meta
    },
    submit () {
      if (!this.canSubmit || this.loading) {
        return
      }
      this.loading = true

      const request = this.mode === 'text'
        ? aiService.ingestText({
          ...this.sharedMeta(),
          content: this.form.content,
          source_url: this.form.source_url || undefined
        })
        : aiService.ingestFile(this.buildFileFormData())

      request.then(result => {
        const queued = result && result.status === 'queued'
        const fileType = result && result.file_type ? result.file_type.toUpperCase() : 'document'
        this.$buefy.notification.open({
          duration: 4000,
          message: queued
            ? `Le ${fileType} <strong>${this.form.title}</strong> a été mis en file de traitement.`
            : `Le document <strong>${this.form.title}</strong> a bien été ajouté.`,
          position: 'is-bottom-right',
          type: 'is-success',
          hasIcon: false
        })
        this.$emit('created', result)
        this.$parent.close()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },
    buildFileFormData () {
      const fd = new FormData()
      const meta = this.sharedMeta()
      Object.keys(meta).forEach(key => {
        if (meta[key] !== undefined && meta[key] !== null) {
          fd.append(key, meta[key])
        }
      })
      fd.append('file', this.file)
      return fd
    },
    notifyError (err) {
      const status = err && err.response ? err.response.status : null
      let message = 'Une erreur est survenue lors de l\'ajout du document.'
      if (status === 403) {
        message = 'Vous n\'avez pas le rôle « ai-admin » requis pour gérer la base de connaissances.'
      } else if (status === 422) {
        const errors = err.response.data && err.response.data.errors
        message = errors ? Object.values(errors).flat().join(' ') : 'Document invalide, vérifiez les champs.'
      }
      this.$buefy.notification.open({
        duration: 5000,
        message,
        position: 'is-bottom-right',
        type: 'is-danger',
        hasIcon: false
      })
    }
  }
}
</script>
